/**
 * Распознавание видеоплощадок.
 *
 * YouTube, Rutube, ВК и подобные не отдают видеофайл — по их адресам лежит
 * страница с плеером. Скачать оттуда поток нельзя (и не нужно): у каждой
 * площадки есть официальный встраиваемый плеер, которым можно управлять из
 * родительской страницы. Именно так работают сервисы совместного просмотра.
 *
 * Здесь мы только разбираем ссылку и говорим, какой плеер нужен. Управление
 * живёт в адаптерах на стороне клиента.
 */
function hostOf(url) {
    return url.hostname.toLowerCase().replace(/^(www|m|mobile)\./, '');
}
/** youtube.com/watch?v=…, youtu.be/…, /shorts/…, /embed/…, /live/… */
function matchYoutube(url, host) {
    let videoId = '';
    if (host === 'youtu.be') {
        videoId = url.pathname.slice(1).split('/')[0] ?? '';
    }
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'music.youtube.com') {
        if (url.pathname === '/watch') {
            videoId = url.searchParams.get('v') ?? '';
        }
        else {
            const match = /^\/(?:embed|shorts|live|v)\/([^/?#]+)/.exec(url.pathname);
            videoId = match?.[1] ?? '';
        }
    }
    // Идентификатор ролика — ровно 11 символов из ограниченного набора.
    if (!/^[\w-]{11}$/.test(videoId))
        return null;
    return {
        kind: 'youtube',
        videoId,
        // Параметры плеера подставляет сам IFrame API, здесь — запасной вариант.
        embedUrl: `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0`,
        label: 'YouTube',
        supportsRate: true,
        accuracy: 'good',
    };
}
/** rutube.ru/video/<id>/, /play/embed/<id>, /shorts/<id> */
function matchRutube(url, host) {
    if (host !== 'rutube.ru')
        return null;
    const match = /^\/(?:video|play\/embed|shorts|video\/private)\/([0-9a-f]{32})/.exec(url.pathname);
    const videoId = match?.[1];
    if (!videoId)
        return null;
    // Приватные ролики открываются только с ключом из адреса.
    const privateKey = url.searchParams.get('p');
    const query = privateKey ? `?p=${encodeURIComponent(privateKey)}` : '';
    return {
        kind: 'rutube',
        videoId,
        embedUrl: `https://rutube.ru/play/embed/${videoId}/${query}`,
        label: 'Rutube',
        supportsRate: false,
        accuracy: 'coarse',
    };
}
/** vk.com/video-123_456, vkvideo.ru/video…, video_ext.php?oid=…&id=…, клипы */
function matchVk(url, host) {
    if (!['vk.com', 'vkvideo.ru', 'vk.ru'].includes(host))
        return null;
    let oid = '';
    let id = '';
    let hash = url.searchParams.get('hash') ?? '';
    if (url.pathname === '/video_ext.php') {
        oid = url.searchParams.get('oid') ?? '';
        id = url.searchParams.get('id') ?? '';
    }
    else {
        const match = /^\/(?:video|clip)(-?\d+)_(\d+)/.exec(url.pathname);
        oid = match?.[1] ?? '';
        id = match?.[2] ?? '';
        // В ссылках вида /video-1_2?list=… ключ доступа лежит в параметре z или hash.
        hash = hash || url.searchParams.get('access_key') || '';
    }
    if (!/^-?\d+$/.test(oid) || !/^\d+$/.test(id))
        return null;
    const params = new URLSearchParams({ oid, id, hd: '2', js_api: '1' });
    if (hash)
        params.set('hash', hash);
    return {
        kind: 'vk',
        videoId: `${oid}_${id}`,
        embedUrl: `https://vk.com/video_ext.php?${params.toString()}`,
        label: 'ВК Видео',
        supportsRate: false,
        accuracy: 'coarse',
    };
}
/** vimeo.com/<id>, player.vimeo.com/video/<id> */
function matchVimeo(url, host) {
    if (host !== 'vimeo.com' && host !== 'player.vimeo.com')
        return null;
    const match = /^\/(?:video\/)?(\d+)/.exec(url.pathname);
    const videoId = match?.[1];
    if (!videoId)
        return null;
    return {
        kind: 'vimeo',
        videoId,
        embedUrl: `https://player.vimeo.com/video/${videoId}`,
        label: 'Vimeo',
        supportsRate: true,
        accuracy: 'good',
    };
}
/** dailymotion.com/video/<id>, dai.ly/<id> */
function matchDailymotion(url, host) {
    let videoId = '';
    if (host === 'dai.ly') {
        videoId = url.pathname.slice(1).split('/')[0] ?? '';
    }
    else if (host === 'dailymotion.com') {
        const match = /^\/video\/([a-z0-9]+)/i.exec(url.pathname);
        videoId = match?.[1] ?? '';
    }
    if (!/^[a-z0-9]{5,12}$/i.test(videoId))
        return null;
    return {
        kind: 'dailymotion',
        videoId,
        embedUrl: `https://www.dailymotion.com/embed/video/${videoId}?api=postMessage`,
        label: 'Dailymotion',
        supportsRate: false,
        accuracy: 'coarse',
    };
}
const MATCHERS = [matchYoutube, matchRutube, matchVk, matchVimeo, matchDailymotion];
/**
 * Определяет, чем открывать ссылку.
 * Возвращает null, если это обычный файл — его играет встроенный плеер.
 */
export function detectProvider(rawUrl) {
    let url;
    try {
        url = new URL(rawUrl.trim());
    }
    catch {
        return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:')
        return null;
    const host = hostOf(url);
    for (const matcher of MATCHERS) {
        const match = matcher(url, host);
        if (match)
            return match;
    }
    return null;
}
/** Список площадок для подсказок в интерфейсе. */
export const SUPPORTED_PROVIDERS = ['YouTube', 'Rutube', 'ВК Видео', 'Vimeo', 'Dailymotion'];
/** Native-only release: providers with an actively connected control API. */
export function detectNativeProvider(url) { const p = detectProvider(url); return p?.kind === 'dailymotion' ? null : p; }
export const NATIVE_PROVIDERS = ['YouTube', 'Rutube', 'ВК Видео', 'Vimeo'];
//# sourceMappingURL=providers.js.map